import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { useAsyncData } from '@/hooks';
import { pipelineService } from '@/services/crm/pipelineService';
import { ROUTES } from '@/constants/routes';
import type { PipelineCaseType } from '@/types/pipeline';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

import DateInput from '@/components/common/DateInput';

export default function PipelineCaseFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultType = (searchParams.get('type') as PipelineCaseType) || 'inquiry';

  const [caseType, setCaseType] = useState<PipelineCaseType>(defaultType);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [contractMonths, setContractMonths] = useState('12');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [submissionDeadline, setSubmissionDeadline] = useState('');
  const [procurementUrl, setProcurementUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { data: customers } = useAsyncData<{ id: string; name: string }[]>(
    '/api/crm/customers',
    { initialData: [], transform: (d) => (Array.isArray(d) ? d : d?.customers || []) }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError('');
    try {
      const created = await pipelineService.createCase({
        case_type: caseType,
        title: title.trim(),
        description: description || undefined,
        customer_id: customerId || undefined,
        estimated_value: estimatedValue ? parseFloat(estimatedValue) : undefined,
        contract_period_months: contractMonths ? parseInt(contractMonths) : undefined,
        reference_number: referenceNumber || undefined,
        submission_deadline: submissionDeadline || undefined,
        procurement_url: procurementUrl || undefined,
        notes: notes || undefined,
      });
      navigate(`${ROUTES.CRM_PIPELINE}/${created.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 bg-bgContainer h-full overflow-auto max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(ROUTES.CRM_PIPELINE)} className="p-2 hover:bg-bgElevated rounded-lg">
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <h1 className="text-2xl font-bold text-textDefault">{t('pipeline.newCase')}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 bg-bgElevated rounded-xl border border-border p-6">
        <div>
          <label className="block text-sm font-medium mb-1">{t('pipeline.caseType')}</label>
          <select
            value={caseType}
            onChange={(e) => setCaseType(e.target.value as PipelineCaseType)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
          >
            <option value="inquiry">{t('pipeline.typeInquiry')}</option>
            <option value="procurement">{t('pipeline.typeProcurement')}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('pipeline.caseTitle')}</label>
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('common.description')}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('crm.customer')}</label>
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
          >
            <option value="">{t('pipeline.noCustomer')}</option>
            {(customers || []).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('pipeline.estimatedValue')}</label>
            <input
              type="number"
              value={estimatedValue}
              onChange={(e) => setEstimatedValue(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('pipeline.contractMonths')}</label>
            <input
              type="number"
              value={contractMonths}
              onChange={(e) => setContractMonths(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
            />
          </div>
        </div>

        {caseType === 'procurement' && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">{t('pipeline.referenceNumber')}</label>
              <input
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('pipeline.submissionDeadline')}</label>
              <DateInput value={submissionDeadline}
                onChange={(e) => setSubmissionDeadline(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('pipeline.procurementUrl')}</label>
              <input
                type="url"
                value={procurementUrl}
                onChange={(e) => setProcurementUrl(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
              />
            </div>
          </>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">{t('common.notes')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-border bg-bgContainer"
          />
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('common.create')}
          </button>
          <button type="button" onClick={() => navigate(ROUTES.CRM_PIPELINE)} className="px-4 py-2 border border-border rounded-lg">
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
